import type Stripe from "stripe";
import { queryOptions } from "@tanstack/react-query";

import {
	apiUrl,
	appFetch,
	ensureOk,
	fetchJson,
	HttpRequestError,
} from "@workspace/base-api";
import { toCamel } from "@workspace/shared";

import type {
	CreateWebhookDto,
	StripeConnectionStatusResponse,
	StripePayment,
	StripePricingGroupRow,
	WebhookEndpoint,
} from "./types";

export const stripeApi = {
	async startCardPayment(
		tenant: string,
		venueId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(`/integrations/stripe/start-payment`, apiUrl);
		const response = await appFetch(url.toString(), {
			method: "POST",
			headers: {
				tenant: tenant || "",
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				venueId,
				...data,
			}),
		});

		// HA-767 — a refusal is a failure. This resolved on any status, so a 4xx/5xx start read as
		// a started payment; the empty-bodied success is why this is not fetchJson.
		if (!response.ok) {
			throw new HttpRequestError("Failed to start the card payment", response);
		}

		return response;
	},
	startCardPaymentMutation: (tenant: string, venueId: string) => {
		return {
			mutationKey: [tenant, venueId, "startCardPayment"],
			mutationFn: (data: Record<string, unknown>) =>
				stripeApi.startCardPayment(tenant, venueId, data),
		};
	},
	async completeCardPayment(
		tenant: string,
		venueId: string,
		paymentId: string,
		data: Record<string, unknown>,
	) {
		const url = new URL(
			`/integrations/stripe/payments/${paymentId}/completed`,
			apiUrl,
		);
		const response = await appFetch(url.toString(), {
			method: "POST",
			headers: {
				tenant: tenant || "",
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				venueId,
				paymentId,
				...data,
			}),
		});

		// HA-767 — as above. The Staff Terminal awaits this before it shows the card as paid, so a
		// refused completion must reject, not resolve.
		if (!response.ok) {
			throw new HttpRequestError("Failed to record the card payment", response);
		}

		return response;
	},
	completeCardPaymentMutation: (
		tenant: string,
		venueId: string,
		paymentId: string,
	) => {
		return {
			mutationKey: [tenant, venueId, paymentId, "completeCardPayment"],
			mutationFn: (data: Record<string, unknown>) =>
				stripeApi.completeCardPayment(tenant, venueId, paymentId, data),
		};
	},
	/**
	 * HA-767 — HA-139: cancel the Payment Intent through the Stripe API. The server refuses with a
	 * 409 when the payment has already been taken or is being processed, which is the answer the
	 * till must act on: it is not cancelled.
	 */
	async cancelCardPayment(tenant: string, paymentId: string) {
		const url = new URL(
			`/integrations/stripe/payments/${paymentId}/cancel`,
			apiUrl,
		);

		// fetchJson for its HttpRequestError: the ProblemDetails detail is the operator's message.
		await fetchJson(url.toString(), {
			method: "POST",
			headers: { tenant: tenant || "" },
			body: JSON.stringify({}),
		});
	},
	getPaymentStatus(tenant: string, paymentId: string) {
		const url = new URL(`/integrations/stripe/payments/${paymentId}`, apiUrl);

		return fetchJson<StripePayment>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getConnectionStatus(tenant: string) {
		const url = new URL(`/integrations/stripe/connection-status`, apiUrl);

		return fetchJson<StripeConnectionStatusResponse>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getConnectionStatusOptions: (tenant: string, enabled = true) => {
		return queryOptions({
			queryKey: [tenant, "stripe", "connectionStatus"],
			queryFn: () => stripeApi.getConnectionStatus(tenant),
			enabled: enabled && !!tenant,
			// HA-298 AC5: the check runs server-side per request, so the page must not serve a
			// cached answer on reload.
			staleTime: 0,
			gcTime: 0,
			refetchOnMount: "always",
			retry: false,
		});
	},
	getPaymentStatusOptions: (
		tenant: string,
		venueId: string,
		paymentId: string,
	) => {
		return queryOptions({
			queryKey: [tenant, venueId, "paymentStatus", paymentId],
			queryFn: () => stripeApi.getPaymentStatus(tenant, paymentId),
			enabled: !!paymentId, // Only run the query if paymentId is provided
		});
	},
};

export const readerApi = {
	async createReader<T>(tenant: string, input: T) {
		const url = new URL(`/integrations/stripe/readers`, apiUrl);

		try {
			const response = await fetchJson<{
				id: string;
			}>(url.toString(), {
				method: "POST",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify({
					...input,
				}),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	createReaderMutation: <T>(tenant: string) => {
		return {
			mutationKey: [tenant, "createReader"],
			mutationFn: (input: T) => readerApi.createReader<T>(tenant, input),
		};
	},
	async renameReader<T>(tenant: string, id: string, input: T) {
		const url = new URL(`/integrations/stripe/readers/${id}`, apiUrl);

		try {
			const response = await fetchJson<{
				id: string;
			}>(url.toString(), {
				method: "POST",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify({
					id,
					...input,
				}),
			});

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	renameReaderMutation: <T>(tenant: string, id: string) => {
		return {
			mutationKey: [tenant, id, "renameReader"],
			mutationFn: (input: T) => readerApi.renameReader<T>(tenant, id, input),
		};
	},
	async reassignReader<T>(tenant: string, id: string, input: T) {
		const url = new URL(`/integrations/stripe/readers/${id}/reassign`, apiUrl);

		try {
			const response = await appFetch(url.toString(), {
				method: "POST",
				headers: {
					tenant: tenant || "",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					id,
					...input,
				}),
			});

			if (!response.ok) {
				throw new HttpRequestError("Failed to reassign reader", response);
			}

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	reassignReaderMutation: <T>(tenant: string, id: string) => {
		return {
			mutationKey: [tenant, id, "reassignReader"],
			mutationFn: (input: T) => readerApi.reassignReader<T>(tenant, id, input),
		};
	},
	async removeReader(tenant: string, id: string) {
		const url = new URL(`/integrations/stripe/readers/${id}`, apiUrl);

		try {
			const response = await appFetch(url.toString(), {
				method: "DELETE",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify({
					id,
				}),
			});

			if (!response.ok) {
				throw new HttpRequestError("Failed to remove reader", response);
			}

			return {
				success: true,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				details: toCamel(await error.response.json()),
				error: error.message,
			};
		}
	},
	removeReaderMutation: (tenant: string) => {
		return {
			mutationKey: [tenant, "remove"],
			mutationFn: (id: string) => readerApi.removeReader(tenant, id),
		};
	},
	getReaders<T>(tenant: string) {
		const url = new URL(`/integrations/stripe/readers`, apiUrl);

		return fetchJson<T[]>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getReadersOptions: <T>(tenant: string) => {
		return queryOptions({
			queryKey: [tenant, "stripe", "readers"],
			queryFn: () => readerApi.getReaders<T>(tenant),
		});
	},
	getReader<T>(tenant: string, id: string) {
		const url = new URL(`/integrations/stripe/readers/${id}`, apiUrl);

		return fetchJson<T>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getReaderOptions: (tenant: string, readerId: string) => {
		return queryOptions({
			queryKey: [tenant, "stripe", "readers", readerId],
			queryFn: () => readerApi.getReader(tenant, readerId),
		});
	},
	getConnectionToken(tenant: string) {
		const url = new URL(
			`/integrations/stripe/terminal/connection-token`,
			apiUrl,
		);

		return fetchJson<{ secret: string }>(url.toString(), {
			method: "POST",
			headers: {
				tenant: tenant || "",
			},
			body: JSON.stringify({}),
		});
	},
	getConnectionTokenMutation: (tenant: string) => {
		return {
			mutationKey: [tenant, "stripe", "terminal", "connectionToken"],
			mutationFn: () => readerApi.getConnectionToken(tenant),
		};
	},

	getLocations(tenant: string, account: string) {
		const url = new URL(`/integrations/stripe/locations/${account}`, apiUrl);

		return fetchJson<Stripe.Terminal.Location[]>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},
	getLocationsOptions: (tenant: string, account: string) => {
		return queryOptions({
			queryKey: [tenant, "stripe", account, "locations"],
			queryFn: () => readerApi.getLocations(tenant, account),
		});
	},
};

// Webhook API endpoints
export const webhookApi = {
	async getEndpoints(tenant: string) {
		const url = new URL(`/integrations/stripe/webhooks`, apiUrl);
		const response = await fetchJson<{
			webhookEndpoints: WebhookEndpoint[];
		}>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});

		return response.webhookEndpoints;
	},

	getEndpointsOptions: (tenant: string) => {
		return queryOptions({
			queryKey: [tenant, "stripe", "webhooks"],
			queryFn: () => webhookApi.getEndpoints(tenant),
		});
	},

	async createEndpoint(tenant: string, body: CreateWebhookDto) {
		const url = new URL(`/integrations/stripe/webhooks`, apiUrl);

		return await fetchJson<WebhookEndpoint>(url.toString(), {
			method: "POST",
			headers: {
				tenant: tenant || "",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		});
	},

	createEndpointMutation: (tenant: string) => {
		return {
			mutationKey: [tenant, "createWebhook"],
			mutationFn: (body: CreateWebhookDto) =>
				webhookApi.createEndpoint(tenant, body),
		};
	},

	async deleteEndpoint(tenant: string, id: string) {
		const url = new URL(
			`/integrations/stripe/webhooks/${encodeURIComponent(id)}`,
			apiUrl,
		);

		return await ensureOk(
			await appFetch(url.toString(), {
				method: "DELETE",
				headers: { tenant: tenant || "" },
				body: JSON.stringify({
					id,
				}),
			}),
		);
	},

	deleteEndpointMutation: (tenant: string) => {
		return {
			mutationKey: [tenant, "deleteWebhook"],
			mutationFn: (id: string) => webhookApi.deleteEndpoint(tenant, id),
		};
	},
};

// HA-1010: the Platform admin's Card pricing groups (system routes, not a tenant's).
export const pricingGroupsApi = {
	async list() {
		const url = new URL(`/system/stripe/pricing-groups`, apiUrl);
		return await fetchJson<StripePricingGroupRow[]>(url.toString(), {
			method: "GET",
		});
	},

	listOptions: () => {
		return queryOptions({
			queryKey: ["system", "stripe", "pricing-groups"],
			queryFn: () => pricingGroupsApi.list(),
		});
	},

	async set(appInstanceId: string, group: string | null) {
		const url = new URL(
			`/system/stripe/pricing-groups/${encodeURIComponent(appInstanceId)}`,
			apiUrl,
		);
		return await fetchJson<StripePricingGroupRow>(url.toString(), {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ group }),
		});
	},

	setMutation: (appInstanceId: string) => {
		return {
			mutationKey: ["system", "stripe", "pricing-groups", appInstanceId],
			mutationFn: (group: string | null) =>
				pricingGroupsApi.set(appInstanceId, group),
		};
	},
};

// Payout API endpoints
export const payoutApi = {
	async getPayoutInfo(tenant: string) {
		const url = new URL(`/integrations/stripe/payouts/info`, apiUrl);
		return await fetchJson<{
			previousPayout: {
				id: string;
				amount: number;
				currency: string;
				arrivalDate: string;
				status: string;
			} | null;
			nextPayout: {
				id: string;
				amount: number;
				currency: string;
				arrivalDate: string;
				status: string;
			} | null;
		}>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getPayoutInfoOptions: (tenant: string) => {
		return queryOptions({
			queryKey: [tenant, "stripe", "payouts", "info"],
			queryFn: () => payoutApi.getPayoutInfo(tenant),
			enabled: !!tenant,
			staleTime: 1000 * 60 * 5, // 5 minutes
		});
	},
};
